import { z } from "zod";

export const DESKTOP_SDK_VERSION = "0.1.0" as const;
export const DESKTOP_PROTOCOL_VERSION = 1 as const;

export const DesktopProtocolVersionSchema = z.literal(DESKTOP_PROTOCOL_VERSION);
export const DesktopKnownErrorCodeSchema = z.enum([
  "CAPABILITY_DENIED",
  "CHALLENGE_EXPIRED",
  "CHALLENGE_REPLAYED",
  "CLIENT_BUSY",
  "CLIENT_DISPOSED",
  "CONTROL_PLANE_TIMEOUT",
  "CONTROL_PLANE_UNAVAILABLE",
  "DESKTOP_REQUIRED",
  "DESKTOP_TIMEOUT",
  "DESKTOP_UNAVAILABLE",
  "DESKTOP_UPGRADE_REQUIRED",
  "DEVICE_STORAGE_UNAVAILABLE",
  "FILE_POLICY_VIOLATION",
  "FILE_SELECTION_CANCELLED",
  "INTERNAL_ERROR",
  "INVALID_REQUEST",
  "INVALID_RESPONSE",
  "OFFLINE_RESTRICTED",
  "OPERATION_DENIED",
  "REQUEST_RATE_LIMITED",
  "REQUEST_REPLAYED",
  "RESOURCE_BUSY",
  "RESOURCE_DISCONNECTED",
  "SESSION_EXPIRED",
  "SESSION_NOT_FOUND",
  "SESSION_REVOKED",
  "TCP_CONNECTION_FAILED",
  "UNTRUSTED_SENDER",
]);
export type DesktopKnownErrorCode = z.infer<typeof DesktopKnownErrorCodeSchema>;

export const DesktopCapabilityKindSchema = z.enum([
  "filesystem.user-selected",
  "http.loopback-listener",
  "serial.balance",
  "tcp.client",
]);

export const BalanceSemanticOperationSchema = z.enum(["readWeight", "tare", "zero"]);
export const FilesystemSemanticOperationSchema = z.enum(["pickFile", "readFile", "writeFile"]);
export const TcpSemanticOperationSchema = z.enum(["connect", "disconnect", "read", "write"]);
export const LoopbackSemanticOperationSchema = z.enum([
  "clearRequests",
  "listRequests",
  "start",
  "status",
  "stop",
]);
export const DesktopSemanticOperationSchema = z.enum([
  "clearRequests",
  "connect",
  "disconnect",
  "listRequests",
  "pickFile",
  "read",
  "readFile",
  "readWeight",
  "start",
  "status",
  "stop",
  "tare",
  "write",
  "writeFile",
  "zero",
]);

export const DESKTOP_OPERATIONS_BY_CAPABILITY = {
  "filesystem.user-selected": ["pickFile", "readFile", "writeFile"],
  "http.loopback-listener": ["clearRequests", "listRequests", "start", "status", "stop"],
  "serial.balance": ["readWeight", "tare", "zero"],
  "tcp.client": ["connect", "disconnect", "read", "write"],
} as const satisfies Record<
  z.infer<typeof DesktopCapabilityKindSchema>,
  readonly z.infer<typeof DesktopSemanticOperationSchema>[]
>;

export const DesktopLogicalBindingSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z][a-z0-9._-]*$/u);
export const DesktopRequestIdSchema = z
  .string()
  .min(8)
  .max(128)
  .regex(/^[A-Za-z0-9_-]+$/u);
export const DesktopSessionHandleSchema = z.string().regex(/^dcs_[A-Za-z0-9_-]{32,128}$/u);
export const DesktopFileGrantIdSchema = z.string().regex(/^dfg_[A-Za-z0-9_-]{24,128}$/u);
export const DesktopTcpConnectionIdSchema = z.string().regex(/^tcp_[A-Za-z0-9_-]{24,128}$/u);
export const DesktopLoopbackRequestIdSchema = z.string().regex(/^lrq_[A-Za-z0-9_-]{24,128}$/u);

export const SortedDesktopOperationsSchema = z
  .array(DesktopSemanticOperationSchema)
  .min(1)
  .max(8)
  .refine(
    (operations) =>
      new Set(operations).size === operations.length &&
      operations.every((operation, index) => {
        const previous = operations.at(index - 1);
        return index === 0 || (previous !== undefined && previous < operation);
      }),
    "Operations must be unique and lexically sorted",
  );

export function operationsMatchCapability(
  capability: z.infer<typeof DesktopCapabilityKindSchema>,
  operations: readonly z.infer<typeof DesktopSemanticOperationSchema>[],
): boolean {
  const allowed = new Set<z.infer<typeof DesktopSemanticOperationSchema>>(
    DESKTOP_OPERATIONS_BY_CAPABILITY[capability],
  );
  return operations.every((operation) => allowed.has(operation));
}

export const BalanceOperationSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("balance.readWeight") }),
  z.strictObject({ type: z.literal("balance.tare") }),
  z.strictObject({ type: z.literal("balance.zero") }),
]);

export const FilesystemOperationSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("filesystem.pick") }),
  z.strictObject({
    type: z.literal("filesystem.read"),
    fileGrantId: DesktopFileGrantIdSchema,
  }),
  z.strictObject({
    type: z.literal("filesystem.write"),
    fileGrantId: DesktopFileGrantIdSchema,
    contentBase64: z
      .string()
      .max(1_398_104)
      .regex(/^[A-Za-z0-9_-]*$/u),
  }),
]);

export const TcpOperationSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("tcp.connect") }),
  z.strictObject({
    type: z.literal("tcp.read"),
    connectionId: DesktopTcpConnectionIdSchema,
    maxBytes: z.number().int().min(1).max(65_536),
    timeoutMs: z.number().int().min(1).max(5_000),
  }),
  z.strictObject({
    type: z.literal("tcp.write"),
    connectionId: DesktopTcpConnectionIdSchema,
    dataBase64: z
      .string()
      .max(87_384)
      .regex(/^[A-Za-z0-9_-]*$/u),
  }),
  z.strictObject({
    type: z.literal("tcp.disconnect"),
    connectionId: DesktopTcpConnectionIdSchema,
  }),
]);

export const LoopbackOperationSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("loopback.clearRequests") }),
  z.strictObject({
    type: z.literal("loopback.listRequests"),
    afterSequence: z.number().int().nonnegative(),
    limit: z.number().int().min(1).max(25),
  }),
  z.strictObject({ type: z.literal("loopback.start") }),
  z.strictObject({ type: z.literal("loopback.status") }),
  z.strictObject({ type: z.literal("loopback.stop") }),
]);

export const DesktopOperationSchema = z.discriminatedUnion("type", [
  ...BalanceOperationSchema.options,
  ...FilesystemOperationSchema.options,
  ...TcpOperationSchema.options,
  ...LoopbackOperationSchema.options,
]);

export const DESKTOP_WIRE_OPERATION_TO_SEMANTIC_OPERATION = {
  "balance.readWeight": "readWeight",
  "balance.tare": "tare",
  "balance.zero": "zero",
  "filesystem.pick": "pickFile",
  "filesystem.read": "readFile",
  "filesystem.write": "writeFile",
  "loopback.clearRequests": "clearRequests",
  "loopback.listRequests": "listRequests",
  "loopback.start": "start",
  "loopback.status": "status",
  "loopback.stop": "stop",
  "tcp.connect": "connect",
  "tcp.disconnect": "disconnect",
  "tcp.read": "read",
  "tcp.write": "write",
} as const satisfies Record<
  z.infer<typeof DesktopOperationSchema>["type"],
  z.infer<typeof DesktopSemanticOperationSchema>
>;

export const DESKTOP_WIRE_OPERATION_TO_CAPABILITY = {
  "balance.readWeight": "serial.balance",
  "balance.tare": "serial.balance",
  "balance.zero": "serial.balance",
  "filesystem.pick": "filesystem.user-selected",
  "filesystem.read": "filesystem.user-selected",
  "filesystem.write": "filesystem.user-selected",
  "loopback.clearRequests": "http.loopback-listener",
  "loopback.listRequests": "http.loopback-listener",
  "loopback.start": "http.loopback-listener",
  "loopback.status": "http.loopback-listener",
  "loopback.stop": "http.loopback-listener",
  "tcp.connect": "tcp.client",
  "tcp.disconnect": "tcp.client",
  "tcp.read": "tcp.client",
  "tcp.write": "tcp.client",
} as const satisfies Record<
  z.infer<typeof DesktopOperationSchema>["type"],
  z.infer<typeof DesktopCapabilityKindSchema>
>;

export function desktopSemanticOperationFor(
  operation: z.infer<typeof DesktopOperationSchema>,
): z.infer<typeof DesktopSemanticOperationSchema> {
  return DESKTOP_WIRE_OPERATION_TO_SEMANTIC_OPERATION[operation.type];
}

export function desktopCapabilityForOperation(
  operation: z.infer<typeof DesktopOperationSchema>,
): z.infer<typeof DesktopCapabilityKindSchema> {
  return DESKTOP_WIRE_OPERATION_TO_CAPABILITY[operation.type];
}

export const DesktopCapabilityRequestSchema = z.strictObject({
  type: z.literal("platform:desktop-capability-request"),
  protocolVersion: DesktopProtocolVersionSchema,
  requestId: DesktopRequestIdSchema,
  binding: DesktopLogicalBindingSchema,
  operations: SortedDesktopOperationsSchema,
});

export const DesktopOperationRequestSchema = z.strictObject({
  type: z.literal("platform:desktop-operation-request"),
  protocolVersion: DesktopProtocolVersionSchema,
  requestId: DesktopRequestIdSchema,
  sessionHandle: DesktopSessionHandleSchema,
  operation: DesktopOperationSchema,
});

export const DesktopCloseRequestSchema = z.strictObject({
  type: z.literal("platform:desktop-capability-close"),
  protocolVersion: DesktopProtocolVersionSchema,
  requestId: DesktopRequestIdSchema,
  sessionHandle: DesktopSessionHandleSchema,
});

export const DesktopOutboundMessageSchema = z.discriminatedUnion("type", [
  DesktopCapabilityRequestSchema,
  DesktopOperationRequestSchema,
  DesktopCloseRequestSchema,
]);

export const DesktopBridgeErrorSchema = z.strictObject({
  code: z.string().min(1).max(64),
  message: z.string().min(1).max(240),
  retryable: z.boolean(),
  operationId: DesktopRequestIdSchema.optional(),
});

const DesktopErrorResultSchema = z.strictObject({
  protocolVersion: DesktopProtocolVersionSchema,
  ok: z.literal(false),
  error: DesktopBridgeErrorSchema,
});

export const DesktopCapabilityActivationDataSchema = z.strictObject({
  sessionHandle: DesktopSessionHandleSchema,
  capability: DesktopCapabilityKindSchema,
  allowedOperations: SortedDesktopOperationsSchema,
  expiresAt: z.number().int().positive(),
});

export const DesktopCapabilityActivationResultSchema = z.discriminatedUnion("ok", [
  z.strictObject({
    protocolVersion: DesktopProtocolVersionSchema,
    ok: z.literal(true),
    data: DesktopCapabilityActivationDataSchema,
  }),
  DesktopErrorResultSchema,
]);

export const DesktopCapabilityResponseSchema = z.strictObject({
  type: z.literal("platform:desktop-capability-response"),
  protocolVersion: DesktopProtocolVersionSchema,
  requestId: DesktopRequestIdSchema,
  result: DesktopCapabilityActivationResultSchema,
});

export const DesktopLoopbackHeaderSchema = z.strictObject({
  name: z.string().min(1).max(64),
  value: z.string().max(1_024),
  redacted: z.boolean(),
});

export const DesktopLoopbackRequestRecordSchema = z.strictObject({
  id: DesktopLoopbackRequestIdSchema,
  sequence: z.number().int().positive(),
  receivedAt: z.number().int().positive(),
  method: z.string().min(1).max(16),
  path: z.string().min(1).max(2_048),
  headers: z.array(DesktopLoopbackHeaderSchema).max(64),
  contentType: z.string().min(1).max(128).optional(),
  bodyText: z.string().max(32_768).optional(),
  bodyBytes: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  bodyTruncated: z.boolean(),
});

export const DesktopLoopbackEndpointSchema = z
  .url()
  .max(256)
  .refine((raw) => {
    try {
      const url = new URL(raw);
      return (
        url.protocol === "http:" &&
        url.hostname === "127.0.0.1" &&
        url.port === "43977" &&
        /^\/capture\/[A-Za-z0-9_-]{22,128}$/u.test(url.pathname) &&
        url.username === "" &&
        url.password === "" &&
        url.search === "" &&
        url.hash === ""
      );
    } catch {
      return false;
    }
  }, "Expected a bounded CCBox loopback capture endpoint");

const BalanceWeightDataSchema = z.strictObject({
  type: z.literal("balance.weight"),
  value: z.number(),
  unit: z.enum(["g", "kg", "lb", "oz"]),
  stable: z.boolean(),
  observedAt: z.number().int().positive(),
});
const BalanceCommandAcceptedDataSchema = z.strictObject({
  type: z.literal("balance.commandAccepted"),
  command: z.enum(["tare", "zero"]),
});
export const BalanceOperationDataSchema = z.discriminatedUnion("type", [
  BalanceWeightDataSchema,
  BalanceCommandAcceptedDataSchema,
]);

const FilesystemPickedDataSchema = z.strictObject({
  type: z.literal("filesystem.picked"),
  fileGrantId: DesktopFileGrantIdSchema,
  displayName: z.string().min(1).max(255),
  size: z.number().int().nonnegative(),
});
const FilesystemReadDataSchema = z.strictObject({
  type: z.literal("filesystem.read"),
  contentBase64: z
    .string()
    .max(1_398_104)
    .regex(/^[A-Za-z0-9_-]*$/u),
  bytesRead: z.number().int().nonnegative().max(1_048_576),
});
const FilesystemWrittenDataSchema = z.strictObject({
  type: z.literal("filesystem.written"),
  bytesWritten: z.number().int().nonnegative().max(1_048_576),
});
export const FilesystemOperationDataSchema = z.discriminatedUnion("type", [
  FilesystemPickedDataSchema,
  FilesystemReadDataSchema,
  FilesystemWrittenDataSchema,
]);

const TcpConnectedDataSchema = z.strictObject({
  type: z.literal("tcp.connected"),
  connectionId: DesktopTcpConnectionIdSchema,
});
const TcpReadDataSchema = z.strictObject({
  type: z.literal("tcp.read"),
  dataBase64: z
    .string()
    .max(87_384)
    .regex(/^[A-Za-z0-9_-]*$/u),
  bytesRead: z.number().int().nonnegative().max(65_536),
  eof: z.boolean(),
});
const TcpWrittenDataSchema = z.strictObject({
  type: z.literal("tcp.written"),
  bytesWritten: z.number().int().nonnegative().max(65_536),
});
const TcpDisconnectedDataSchema = z.strictObject({
  type: z.literal("tcp.disconnected"),
  connectionId: DesktopTcpConnectionIdSchema,
});
export const TcpOperationDataSchema = z.discriminatedUnion("type", [
  TcpConnectedDataSchema,
  TcpReadDataSchema,
  TcpWrittenDataSchema,
  TcpDisconnectedDataSchema,
]);

const LoopbackStartedDataSchema = z.strictObject({
  type: z.literal("loopback.started"),
  endpoint: DesktopLoopbackEndpointSchema,
});
const LoopbackStatusDataSchema = z.strictObject({
  type: z.literal("loopback.status"),
  running: z.boolean(),
  endpoint: DesktopLoopbackEndpointSchema.optional(),
  bufferedRequests: z.number().int().nonnegative().max(50),
  latestSequence: z.number().int().nonnegative(),
});
const LoopbackRequestsDataSchema = z.strictObject({
  type: z.literal("loopback.requests"),
  requests: z.array(DesktopLoopbackRequestRecordSchema).max(25),
  latestSequence: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});
const LoopbackClearedDataSchema = z.strictObject({
  type: z.literal("loopback.cleared"),
  cleared: z.number().int().nonnegative().max(50),
});
const LoopbackStoppedDataSchema = z.strictObject({
  type: z.literal("loopback.stopped"),
});
export const LoopbackOperationDataSchema = z.discriminatedUnion("type", [
  LoopbackStartedDataSchema,
  LoopbackStatusDataSchema,
  LoopbackRequestsDataSchema,
  LoopbackClearedDataSchema,
  LoopbackStoppedDataSchema,
]);

export const DesktopOperationDataSchema = z.discriminatedUnion("type", [
  ...BalanceOperationDataSchema.options,
  ...FilesystemOperationDataSchema.options,
  ...TcpOperationDataSchema.options,
  ...LoopbackOperationDataSchema.options,
]);

export const DesktopOperationResultSchema = z.discriminatedUnion("ok", [
  z.strictObject({
    protocolVersion: DesktopProtocolVersionSchema,
    ok: z.literal(true),
    data: DesktopOperationDataSchema,
  }),
  DesktopErrorResultSchema,
]);

export const DesktopOperationResponseSchema = z.strictObject({
  type: z.literal("platform:desktop-operation-response"),
  protocolVersion: DesktopProtocolVersionSchema,
  requestId: DesktopRequestIdSchema,
  result: DesktopOperationResultSchema,
});

export const DesktopCloseResultSchema = z.discriminatedUnion("ok", [
  z.strictObject({
    protocolVersion: DesktopProtocolVersionSchema,
    ok: z.literal(true),
    data: z.strictObject({ closed: z.literal(true) }),
  }),
  DesktopErrorResultSchema,
]);

export const DesktopCloseResponseSchema = z.strictObject({
  type: z.literal("platform:desktop-capability-close-response"),
  protocolVersion: DesktopProtocolVersionSchema,
  requestId: DesktopRequestIdSchema,
  result: DesktopCloseResultSchema,
});

export const DesktopSessionStateSchema = z.enum([
  "active",
  "grace",
  "revoked",
  "expired",
  "closed",
]);

export const DesktopSessionEventSchema = z.strictObject({
  type: z.literal("platform:desktop-session-event"),
  protocolVersion: DesktopProtocolVersionSchema,
  sessionHandle: DesktopSessionHandleSchema,
  event: z.strictObject({
    type: z.enum(["stateChanged", "dataAvailable", "resourceDisconnected"]),
    state: DesktopSessionStateSchema.optional(),
    occurredAt: z.number().int().positive(),
  }),
});

export const DesktopInboundMessageSchema = z.discriminatedUnion("type", [
  DesktopCapabilityResponseSchema,
  DesktopOperationResponseSchema,
  DesktopCloseResponseSchema,
  DesktopSessionEventSchema,
]);

// Wire-name aliases preserve the existing platform contract while giving app
// authors shorter SDK names.
export const DesktopClientRequestIdSchema = DesktopRequestIdSchema;
export const DesktopLocalSessionHandleSchema = DesktopSessionHandleSchema;
export const DesktopOperationNameSchema = DesktopSemanticOperationSchema;
export const DesktopCapabilityRequestMessageSchema = DesktopCapabilityRequestSchema;
export const DesktopOperationRequestMessageSchema = DesktopOperationRequestSchema;
export const DesktopSessionCloseMessageSchema = DesktopCloseRequestSchema;
export const DesktopFrameMessageSchema = DesktopOutboundMessageSchema;
export const DesktopCapabilityResponseMessageSchema = DesktopCapabilityResponseSchema;
export const DesktopOperationResponseMessageSchema = DesktopOperationResponseSchema;
export const DesktopCloseResponseMessageSchema = DesktopCloseResponseSchema;
export const DesktopSessionEventMessageSchema = DesktopSessionEventSchema;

export type DesktopCapabilityKind = z.infer<typeof DesktopCapabilityKindSchema>;
export type DesktopSemanticOperation = z.infer<typeof DesktopSemanticOperationSchema>;
export type DesktopOperation = z.infer<typeof DesktopOperationSchema>;
export type BalanceOperation = z.infer<typeof BalanceOperationSchema>;
export type FilesystemOperation = z.infer<typeof FilesystemOperationSchema>;
export type TcpOperation = z.infer<typeof TcpOperationSchema>;
export type LoopbackOperation = z.infer<typeof LoopbackOperationSchema>;
export type DesktopCapabilityActivationData = z.infer<typeof DesktopCapabilityActivationDataSchema>;
export type DesktopFrameMessage = z.infer<typeof DesktopFrameMessageSchema>;
export type DesktopOperationRequest = DesktopOperation;
export type DesktopCapabilityActivationResult = z.infer<
  typeof DesktopCapabilityActivationResultSchema
>;
export type DesktopOperationResult = z.infer<typeof DesktopOperationResultSchema>;
export type DesktopCloseResult = z.infer<typeof DesktopCloseResultSchema>;
export type DesktopOperationData = z.infer<typeof DesktopOperationDataSchema>;
export type BalanceOperationData = z.infer<typeof BalanceOperationDataSchema>;
export type FilesystemOperationData = z.infer<typeof FilesystemOperationDataSchema>;
export type TcpOperationData = z.infer<typeof TcpOperationDataSchema>;
export type LoopbackOperationData = z.infer<typeof LoopbackOperationDataSchema>;
export type DesktopLoopbackRequestRecord = z.infer<typeof DesktopLoopbackRequestRecordSchema>;
export type DesktopSessionEvent = z.infer<typeof DesktopSessionEventSchema>;
export type DesktopSessionState = z.infer<typeof DesktopSessionStateSchema>;
